import {
  Button,
  Checkbox,
  DatePicker,
  InputNumber,
  Select,
  Space,
  Switch,
  TableProps,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { Fragment, useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import FeedbackIcon from "../../../../assets/icons/FeedbackIcon";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import SectionHeader from "../../../../components/common/SectionHeader";
import SuccessModal from "../../../../components/Report/SuccessModal";
import ManageNewLocationModal from "../../../../components/Settings/OfficeSetting/ManageNewLocationModal";
import ManageWifi from "../../../../components/Settings/OfficeSetting/ManageWifi";
import SelectEmployeeWithCheckbox from "../../../../components/Settings/OfficeSetting/SelectEmployeeWithCheckbox";
import ViewSelectedUserModal from "../../../../components/Settings/OfficeSetting/ViewSelectedUserModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import { dbTimeFormat, timeFormat } from "../../../../constants/constants";
import {
  useAddOfficeSettingMutation,
  useGetOfficeSettingDataQuery,
} from "../../../../features/attendanceSetting/attendanceSettingApi";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import {
  useGetBusinessDataQuery,
  useGetEmployeesQuery,
} from "../../../../features/employee/employeeApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { ViewSelectedUserParams } from "../../../../types/attendance";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";
import { officeSettingsTabList } from "./officeSettingsTabList";

type ModalType =
  | "ADD_LOCATION"
  | "UPDATE_LOCATION"
  | "ADD_WIFI"
  | "UPDATE_WIFI"
  | "VIEW_SELECTED"
  | "SUCCESS";

interface IAttendanceType {
  id: number | null;
  type: "location_based" | "remote" | "ip_based";
  action: "checked" | "unchecked";
  employee_id: number[];
}

export interface IGeoLocation {
  office_name: string;
  lat: string;
  lng: string;
  radius: number;
  action?: "add" | "edit" | "delete";
  id: number | null;
}

export interface IWifi {
  id: number | null;
  office_name: string;
  ip: string;
  action?: "add" | "edit" | "delete";
}

export default function Operational() {
  const { open, handleClose, handleOpen } = useModal<any, ModalType>();
  const [attendance_types, setAttendance_types] = useState<IAttendanceType[]>(
    []
  );
  const { authInfo } = useAppSelector((state) => state.auth);
  const [business_geo_locations, setBusiness_geo_locations] = useState<
    IGeoLocation[]
  >([]);
  const [business_offices, setBusiness_offices] = useState<IWifi[]>([]);
  const [weekends, setWeekends] = useState<string[]>([]);
  const [working_days_type, setWorking_days_type] = useState("as_per_calendar");
  const [weekend_pattern, setWeekend_pattern] =
    useState<keyof typeof weekendPatternTitle>("regular");
  const [is_weekend_included, setIs_weekend_included] = useState(false);
  const [is_auto_wifi_attendance_enable, setIs_auto_wifi_attendance_enable] =
    useState(false);
  const [auto_wifi_attendance_start_time, setAuto_wifi_attendance_start_time] =
    useState<Dayjs | null>(null);
  const [daysCount, setDaysCount] = useState<number | null>(null);
  const [departmentId, setDepartmentId] = useState<{
    id?: number;
    type?: string;
  }>();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
    isLoading: isEmployeeLoading,
  } = useGetEmployeesQuery({
    params: {
      limit: 0,
      skip: 0,
      status: "active",
      // ...(departmentId
      //   ? { business_department_id: departmentId?.toString() }
      //   : {}),
    },
  });
  const { isLoading, isError, error, data } = useGetOfficeSettingDataQuery({});
  const [
    addOfficeSetting,
    {
      isLoading: isAddLoading,
      isError: isAddError,
      error: addError,
      isSuccess: isAddSuccess,
    },
  ] = useAddOfficeSettingMutation();
  const {
    data: businessData,
    isError: isBusinessError,
    error: businessError,
    isLoading: isBusinessLoading,
  } = useGetBusinessDataQuery(authInfo?.business_id?.toString() || "", {
    skip: !authInfo?.business_id,
  });
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isError, error, "Get optional setting data fail");
  useError(isAddError, addError, "Update optional setting fail");
  useError(isBusinessError, businessError, "Get business setting fail");

  // console.log(attendance_types);
  const isShowAutoWifi =
    businessData?.data?.business?.is_auto_wifi_attendance_accessible;

  // submit handler
  const handleSubmit = () => {
    function areAllEmployeeIdsEmpty(): boolean {
      return attendance_types?.every((item) => item?.employee_id?.length === 0);
    }
    // const findAttendanceType = attendance_types?.find(
    //   (item) => item?.action === "checked" && !item?.employee_id?.length
    // )?.type;

    // if (findAttendanceType) {
    //   toast(
    //     "error",
    //     `Please select at least one employee in ${underscoreToCapitalizeString(
    //       findAttendanceType
    //     )}`
    //   );
    //   return;
    // }

    if (areAllEmployeeIdsEmpty()) {
      toast("error", `Please select at least one employee in attendance type`);
      return;
    }

    const locations = business_geo_locations
      ?.filter((item) => item?.action)
      ?.map((item) => ({
        id:
          item?.action === "add" || typeof item?.id === "string"
            ? undefined
            : item?.id,
        office_name: item?.office_name,
        action:
          typeof item?.id === "string" &&
          (item?.action === "add" || item?.action === "edit")
            ? "add"
            : item?.action,
        lat: item?.lat?.toString(),
        lng: item?.lng?.toString(),
        radius: Number(item?.radius),
      }));

    const wifiList = business_offices
      ?.filter((item) => item?.action)
      ?.map((item) => ({
        ...item,
        id:
          item?.action === "add" || typeof item?.id === "string"
            ? undefined
            : item?.id,
        action:
          typeof item?.id === "string" &&
          (item?.action === "add" || item?.action === "edit")
            ? "add"
            : item?.action,
      }));

    if (
      attendance_types?.find((el) => el?.type === "location_based")?.action ===
        "checked" &&
      !attendance_types?.find((el) => el?.type === "location_based")
        ?.employee_id?.length
    ) {
      toast("error", `Please add at least one employee in location`);
      return;
    }
    if (
      attendance_types?.find((el) => el?.type === "remote")?.action ===
        "checked" &&
      !attendance_types?.find((el) => el?.type === "remote")?.employee_id
        ?.length
    ) {
      toast("error", `Please add at least one employee in remote`);
      return;
    }
    if (
      attendance_types?.find((el) => el?.type === "ip_based")?.action ===
        "checked" &&
      !attendance_types?.find((el) => el?.type === "ip_based")?.employee_id
        ?.length
    ) {
      toast("error", `Please add at least one employee in wifi`);
      return;
    }

    const locationsActiveList = business_geo_locations?.filter(
      (item) => item?.action !== "delete"
    );
    const wifiActiveList = business_offices?.filter(
      (item) => item?.action !== "delete"
    );

    if (
      attendance_types?.find((el) => el?.type === "location_based")?.action ===
        "checked" &&
      !locationsActiveList?.length
    ) {
      toast("error", `Please add at least one location`);
      return;
    }

    if (
      attendance_types?.find((el) => el?.type === "ip_based")?.action ===
        "checked" &&
      !wifiActiveList?.length
    ) {
      toast("error", `Please add at least one wifi`);
      return;
    }

    const values = {
      attendance_types: attendance_types
        ?.filter((item) => item?.action === "checked")
        ?.map((item) => ({
          type: item.type,
          employee_id: item.employee_id,
          // action: item?.action,
        })),
      business_geo_locations: locations,
      business_offices: wifiList,
      weekends,
      working_days_type,
      weekend_pattern,
      days: daysCount,
      is_weekend_included: is_weekend_included ? 1 : 0,
      is_auto_wifi_attendance_enable: is_auto_wifi_attendance_enable ? 1 : 0,
      auto_wifi_attendance_start_time: auto_wifi_attendance_start_time
        ? dayjs(auto_wifi_attendance_start_time).format(dbTimeFormat)
        : undefined,
    };

    addOfficeSetting(values);
  };

  const findAttendanceType = (type: IAttendanceType["type"]) => {
    return (
      attendance_types?.find((item) => item.type === type)?.action === "checked"
    );
  };

  const handleAttendanceType = (
    checked: boolean,
    type: IAttendanceType["type"]
  ) => {
    setAttendance_types((prev) =>
      prev?.map((item) => {
        if (item?.type === type) {
          if (checked) {
            toast("success", `Successfully Added!`);
          } else {
            toast("success", `Successfully Deleted!`);
          }
          return {
            ...item,
            action: checked ? "checked" : "unchecked",
            employee_id: checked ? item?.employee_id : [],
          };
        }

        return item;
      })
    );
  };

  // const handleSelectedEmployee = (
  //   employee_id: IAttendanceType["employee_id"],
  //   type: IAttendanceType["type"]
  // ) => {
  //   setAttendance_types((prev) =>
  //     prev.map((item) => {
  //       if (item?.type === type) {
  //         return {
  //           ...item,
  //           employee_id,
  //         };
  //       }

  //       return item;
  //     })
  //   );
  // };

  const resetAttendanceType = () => {
    setAttendance_types((prev) =>
      prev?.map((item) => {
        return {
          ...item,
          action: "unchecked",
          employee_id: [],
        };
      })
    );

    setDepartmentId({ type: undefined, id: undefined });
  };

  const manageLocation = (val: IGeoLocation, idx?: number) => {
    if (val?.action === "add") {
      setBusiness_geo_locations((prev) => [val, ...prev]);
    } else if (val?.action === "edit") {
      setBusiness_geo_locations((prev) =>
        prev?.map((item, index) => {
          if (idx === index) {
            return val;
          }

          return item;
        })
      );
    }
  };

  const handleEmployeeChange = (type: string, empId?: number[]) => {
    setAttendance_types((prev) =>
      prev?.map((item) => ({
        ...item,
        ...(item?.type === type ? { employee_id: empId } : {}),
      }))
    );
  };

  const removeLocation = (id: number | string) => {
    setBusiness_geo_locations((prev) =>
      prev?.map((item) => ({
        ...item,
        action: id === item?.id ? "delete" : item.action,
      }))
    );

    toast("success", "Successfully Deleted!");
  };

  const manageWifi = (val: IWifi, idx?: number) => {
    if (val?.action === "add") {
      setBusiness_offices((prev) => [val, ...prev]);
    } else if (val?.action === "edit") {
      setBusiness_offices((prev) =>
        prev?.map((item, index) => {
          if (idx === index) {
            return val;
          }

          return item;
        })
      );
    }
  };

  const removeWifi = (id: number | string) => {
    setBusiness_offices((prev) =>
      prev?.map((item) => ({
        ...item,
        action: id === item?.id ? "delete" : item.action,
      }))
    );

    toast("success", "Successfully Deleted!");
  };

  function removeMatchingElements<T>(
    arr1: T[],
    arr2: T[],
    isDuplicate?: boolean
  ): T[] {
    // TODO: Hide condition as per new requirement
    // if (isDuplicate || isDuplicate === undefined) {
    //   return arr1;
    // } else {
    //   const set2 = new Set(arr2);
    //   return arr1?.filter((item) => !set2.has(item));
    // }

    return arr1;
  }

  // Table Columns for Location
  const locationColumns: TableProps<IGeoLocation>["columns"] = [
    {
      title: "SL#",
      render: (_, __, idx) => idx + 1,
      align: "center",
      width: 70,
    },
    { title: "Name", dataIndex: "office_name" },
    { title: "Lat , Long", render: (val) => `${val?.lat}, ${val?.lng}` },
    { title: "Area Radius", dataIndex: "radius" },
    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val, __, idx) => (
        <Space>
          <Button
            className="action-button"
            size="small"
            icon={<EditIcon />}
            onClick={() =>
              handleOpen({
                type: "UPDATE_LOCATION",
                data: { data: val, idx },
              })
            }
          />
          <Button
            className="action-button"
            size="small"
            icon={<DeleteIcon />}
            onClick={() => removeLocation(val?.id)}
          />
        </Space>
      ),
    },
  ];

  // Table Columns for Wifi
  const wifiColumns: TableProps<any>["columns"] = [
    {
      title: "SL#",
      render: (_, __, idx) => idx + 1,
      align: "center",
      width: 70,
    },
    { title: "Name", dataIndex: "office_name" },
    { title: "Wifi", dataIndex: "ip" },
    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val, _, idx) => (
        <Space>
          <Button
            className="action-button"
            size="small"
            icon={<EditIcon />}
            onClick={() =>
              handleOpen({
                type: "UPDATE_WIFI",
                data: { data: val, idx },
              })
            }
          />
          <Button
            className="action-button"
            size="small"
            icon={<DeleteIcon />}
            onClick={() => removeWifi(val?.id)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const settingData = data?.data?.office_settings_operational;

    const attendanceTypes = settingData?.sheba_attendance_types?.map(
      (type) => ({
        type: type?.value,
        employee_id: type?.employee_id || [],
        action: type?.employee_id?.length ? "checked" : "unchecked",
      })
    ) as IAttendanceType[];

    setAttendance_types(attendanceTypes);

    const locationList = settingData?.business_office_locations?.map(
      (item) => ({
        office_name: item?.location_name,
        lat: item?.lat,
        lng: item?.lng,
        radius: item?.radius,
        id: item?.id || null,
      })
    ) as IGeoLocation[];

    setBusiness_geo_locations(locationList);

    const wifiList = settingData?.business_offices?.map((item) => ({
      ip: item?.ip,
      office_name: item?.office_name,
      id: item?.id || null,
    })) as IWifi[];

    setBusiness_offices(wifiList);

    setWeekends(settingData?.weekends || []);

    setWorking_days_type(
      settingData?.total_working_days_type || "as_per_calendar"
    );
    setWeekend_pattern(settingData?.weekend_pattern || "regular");

    setIs_weekend_included(settingData?.is_weekend_included ? true : false);
    setIs_auto_wifi_attendance_enable(
      settingData?.is_auto_wifi_attendance_enable ? true : false
    );
    setDaysCount(settingData?.total_working_days || null);
    setAuto_wifi_attendance_start_time(
      settingData?.auto_wifi_attendance_start_time
        ? dayjs(settingData?.auto_wifi_attendance_start_time, dbTimeFormat)
        : null
    );
  }, [data?.data?.office_settings_operational]);

  useEffect(() => {
    const settingData = data?.data?.office_settings_operational;
    if (settingData) {
      setIs_weekend_included(settingData?.is_weekend_included ? true : false);
      setDaysCount(settingData?.total_working_days || null);
    }
  }, [data?.data?.office_settings_operational]);

  const handleWeekendCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setIs_weekend_included(checked);

    if (checked) {
      toast("success", "Successfully Added!");
    } else {
      toast("success", "Successfully Deleted!");
    }
  };

  // success toast message
  useEffect(() => {
    if (isAddSuccess) {
      toast("success", "Operational setting successfully updated!");
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddSuccess]);

  const findAction = (type: IAttendanceType["type"]) => {
    return (
      attendance_types?.find((item) => item?.type === type)?.action ===
      "checked"
    );
  };

  const findActionEmployee = (type: IAttendanceType["type"], id: number) => {
    return attendance_types?.find(
      (item) =>
        item?.type === type && item?.employee_id?.find((el) => el === id)
    );
  };

  useEffect(() => {
    const settingData = data?.data?.office_settings_operational;
    if (settingData) {
      setWeekends(settingData?.weekends || []);
    }
  }, [data?.data?.office_settings_operational]);

  const handleWeekendDayCheckboxChange = (val: string[]) => {
    // Check for added or removed days
    const added = val.filter((day) => !weekends?.includes(day));
    const removed = weekends?.filter((day) => !val.includes(day));

    // Show success toast when a day is added
    if (added?.length > 0) {
      // toast("success", `Successfully Added: ${added.join(", ")}`);
      toast("success", `Successfully Added!`);
    }

    // Show error toast when a day is removed
    if (removed?.length > 0) {
      toast("success", `Successfully Deleted!`);
    }

    setWeekends(val);
  };

  const departmentListData = [
    { label: "All", value: 0 },
    ...(departmentData?.data?.departments?.map((item) => ({
      label: item?.name,
      value: item?.id,
    })) || []),
  ];

  // test

  return (
    <SidebarLayout
      title="Office Settings"
      contentClass="!pb-0"
      tabList={officeSettingsTabList}
    >
      {open?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={open?.open}
          handleClose={handleClose}
        />
      )}

      {(open.type === "ADD_LOCATION" || open.type === "UPDATE_LOCATION") && (
        <ManageNewLocationModal
          open={open.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleClose}
          handleManage={manageLocation}
        />
      )}
      {(open.type === "ADD_WIFI" || open.type === "UPDATE_WIFI") && (
        <ManageWifi
          open={open.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleClose}
          handleManage={manageWifi}
        />
      )}
      {open.type === "VIEW_SELECTED" && (
        <ViewSelectedUserModal
          open={open.open}
          data={open?.data as ViewSelectedUserParams["type"]}
          handleClose={handleClose}
        />
      )}

      <div className="p-6 pb-0 bg-white rounded-md border border-extra-light">
        <h5 className="text-header text-[22px] font-semibold mb-6">
          Operational Information
        </h5>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            {isEmployeeLoading || isBusinessLoading ? (
              <div className="pb-5">
                <Loader />
              </div>
            ) : (
              <div>
                <SectionHeader
                  title="Attendance Type"
                  buttonText="Reset All"
                  onButtonClick={resetAttendanceType}
                />
                <div className="grid md:grid-cols-3 items-center gap-y-6 my-6">
                  {data?.data?.office_settings_operational?.sheba_attendance_types?.map(
                    (item) => {
                      return (
                        <Fragment key={item?.title}>
                          <div>
                            <Checkbox
                              className="text-header text-sm font-medium"
                              checked={findAttendanceType(
                                item?.value as IAttendanceType["type"]
                              )}
                              onChange={(e) =>
                                handleAttendanceType(
                                  e.target.checked,
                                  item?.value as IAttendanceType["type"]
                                )
                              }
                            >
                              {item?.title}
                              <span className="text-[#88909f] text-sm font-normal">
                                {item?.subtitle}
                              </span>
                            </Checkbox>
                            {/* <div className="text-header text-sm font-medium text-nowrap">
                            {idx + 1}. {item?.title}
                            <span className="text-[#88909f] text-sm font-normal">
                              {item?.subtitle}
                            </span>
                          </div> */}
                          </div>
                          <div className="md:col-span-2 flex justify-between items-center pr-10">
                            <div className="flex gap-2 flex-wrap flex-1">
                              <Select
                                placeholder="Select"
                                allowClear
                                showSearch
                                className="min-w-[130px]"
                                filterOption={selectSearchOption}
                                options={departmentListData}
                                // options={selectArrayOption(
                                //   departmentData?.data?.departments || [],
                                //   "name",
                                //   "id"
                                // )}
                                suffixIcon={<SelectDropdownLgIcon />}
                                value={
                                  item?.value === departmentId?.type
                                    ? departmentId?.id
                                    : 0
                                }
                                onChange={(val) => {
                                  setDepartmentId({
                                    id: val,
                                    type: item?.value,
                                  });
                                }}
                              />
                              <SelectEmployeeWithCheckbox
                                value={
                                  attendance_types?.find(
                                    (att) => att?.type === item?.value
                                  )?.employee_id
                                }
                                disabled={
                                  !findAttendanceType(
                                    item?.value as IAttendanceType["type"]
                                  )
                                }
                                onChange={(emp, isDuplicate, isAll) => {
                                  if (isAll && isDuplicate) {
                                    if (item?.value === "location_based") {
                                      // handleEmployeeChange("ip_based", []);
                                      handleEmployeeChange(item?.value, emp);
                                      setAttendance_types((prev) =>
                                        prev?.map((el) => {
                                          if (el?.type === "ip_based") {
                                            return {
                                              ...el,
                                              employee_id: emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    false
                                                  )
                                                : el?.employee_id,
                                            };
                                          }

                                          return el;
                                        })
                                      );
                                      return;
                                    } else if (item?.value === "ip_based") {
                                      // handleEmployeeChange("location_based", []);
                                      handleEmployeeChange(item?.value, emp);
                                      setAttendance_types((prev) =>
                                        prev?.map((el) => {
                                          if (el?.type === "location_based") {
                                            return {
                                              ...el,
                                              employee_id: emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    false
                                                  )
                                                : el?.employee_id,
                                            };
                                          }

                                          return el;
                                        })
                                      );
                                      return;
                                    }

                                    handleEmployeeChange(item?.value, emp);

                                    return;
                                  }

                                  if (item?.value === "location_based") {
                                    handleEmployeeChange(item?.value, emp);
                                    setAttendance_types((prev) =>
                                      prev?.map((el) => {
                                        if (el?.type === "ip_based") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        if (el?.type === "remote") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        return el;
                                      })
                                    );
                                  } else if (item?.value === "ip_based") {
                                    handleEmployeeChange(item?.value, emp);
                                    setAttendance_types((prev) =>
                                      prev?.map((el) => {
                                        if (el?.type === "location_based") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        if (el?.type === "remote") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        return el;
                                      })
                                    );
                                  } else if (item?.value === "remote") {
                                    handleEmployeeChange(item?.value, emp);
                                    setAttendance_types((prev) =>
                                      prev?.map((el) => {
                                        if (el?.type === "location_based") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        if (el?.type === "ip_based") {
                                          return {
                                            ...el,
                                            employee_id:
                                              el?.employee_id?.length &&
                                              emp?.length
                                                ? removeMatchingElements(
                                                    el?.employee_id,
                                                    emp,
                                                    isDuplicate
                                                  )
                                                : el?.employee_id,
                                          };
                                        }

                                        return el;
                                      })
                                    );
                                  } else {
                                    handleEmployeeChange(item?.value, emp);
                                  }
                                }}
                                data={employeeData?.data?.employees
                                  ?.filter((el) =>
                                    item?.title === "Auto Wifi based"
                                      ? el?.is_wifi_network_active === 1
                                      : true
                                  )
                                  ?.map((employee) => ({
                                    name: employee.member?.profile?.name,
                                    employeeId: employee?.employee_id,
                                    id: employee?.id,
                                    departmentId:
                                      employee?.role?.department?.id,
                                    alreadySet: findActionEmployee(
                                      "location_based",
                                      employee?.id
                                    )
                                      ? "Location"
                                      : findActionEmployee(
                                          "ip_based",
                                          employee?.id
                                        )
                                      ? "Wifi"
                                      : findActionEmployee(
                                          "remote",
                                          employee?.id
                                        )
                                      ? "Remote"
                                      : undefined,
                                  }))}
                                departmentId={
                                  item?.value === departmentId?.type
                                    ? departmentId?.id
                                    : undefined
                                }
                                type={item?.value as IAttendanceType["type"]}
                              />
                            </div>
                            <Button
                              className="action-button"
                              size="small"
                              icon={<ViewIcon />}
                              onClick={() =>
                                handleOpen({
                                  type: "VIEW_SELECTED",
                                  data: item?.value,
                                })
                              }
                            />
                          </div>
                        </Fragment>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {!!isShowAutoWifi && (
              <div className="grid md:grid-cols-3">
                <div className="mb-6 flex items-center gap-2">
                  <Switch
                    id="wifiAutoAttendance"
                    checked={is_auto_wifi_attendance_enable}
                    onChange={setIs_auto_wifi_attendance_enable}
                  />
                  <label
                    htmlFor="wifiAutoAttendance"
                    className="text-header text-sm cursor-pointer font-medium select-none"
                  >
                    Wifi Auto Attendance
                  </label>

                  <button className="text-secondary text-xs font-medium uppercase px-2 py-0.5 bg-white rounded border border-secondary h-6 justify-center items-center inline-flex ml-1.5 gap-1 cursor-default">
                    <FeedbackIcon />
                    beta
                  </button>
                </div>
                <div className="mb-6 flex items-center gap-2">
                  <DatePicker.TimePicker
                    placeholder="HH:MM"
                    style={{ width: "120px" }}
                    format={timeFormat}
                    // GS-4 Start
                    value={dayjs(auto_wifi_attendance_start_time, "HH:mm")}
                    // GS-4 End
                    onChange={(props) =>
                      setAuto_wifi_attendance_start_time(props)
                    }
                  />
                  <label className="text-header text-sm cursor-pointer font-medium select-none">
                    Wifi Auto Attendance Start Time
                  </label>
                </div>
              </div>
            )}

            {/* List of Location */}
            {findAction("location_based") ? (
              <div className="mb-6">
                <SectionHeader
                  title="List of Location"
                  buttonText="Add Location"
                  icon={<AddIcon />}
                  onButtonClick={() =>
                    handleOpen({
                      type: "ADD_LOCATION",
                    })
                  }
                  className="mb-6"
                />
                <div className="max-h-96 overflow-y-auto">
                  <CustomTable
                    dataSource={addKeyInArray(
                      business_geo_locations?.filter(
                        (item) => item?.action !== "delete"
                      )
                    )}
                    columns={locationColumns}
                  />
                </div>
              </div>
            ) : null}

            {/* List of Wifi */}
            {findAction("ip_based") ? (
              <div className="mb-6">
                <SectionHeader
                  title="List of Wifi"
                  buttonText="Add Wifi"
                  icon={<AddIcon />}
                  onButtonClick={() =>
                    handleOpen({
                      type: "ADD_WIFI",
                    })
                  }
                  className="mb-6"
                />

                <div className="max-h-96 overflow-y-auto">
                  <CustomTable
                    dataSource={addKeyInArray(
                      business_offices?.filter(
                        (item) => item?.action !== "delete"
                      )
                    )}
                    columns={wifiColumns}
                  />
                </div>
              </div>
            ) : null}

            {/* Weekend */}
            <div>
              <SectionHeader
                title="Weekend"
                buttonText="Reset All"
                onButtonClick={() => setWeekends([])}
                className="mb-6"
              />
              {/* TODO: */}

              <div className="flex items-center gap-4 mb-6">
                <Select
                  placeholder="Select"
                  size="large"
                  filterOption={selectSearchOption}
                  options={weekendPatternList}
                  suffixIcon={<SelectDropdownLgIcon />}
                  style={{ width: 360 }}
                  value={weekend_pattern}
                  onChange={(val) => setWeekend_pattern(val)}
                />
                <p className="text-[#88909f] text-sm">
                  - {weekendPatternTitle[weekend_pattern]}
                </p>
              </div>
              <Checkbox.Group
                className="grid grid-cols-4 gap-2 md:max-w-[800px]"
                value={weekends}
                // onChange={(val) => setWeekends(val as string[])}
                onChange={(val) =>
                  handleWeekendDayCheckboxChange(val as string[])
                }
              >
                {daysList?.map((day) => (
                  <Checkbox
                    value={day}
                    className="text-header text-sm font-medium"
                    key={day}
                  >
                    {day}
                  </Checkbox>
                ))}
              </Checkbox.Group>
              <br />
              <div className="custom-form">
                <h5 className="text-body text-sm font-medium mb-2">
                  Total Working Days
                </h5>
                <Select
                  placeholder="Select"
                  size="large"
                  filterOption={selectSearchOption}
                  options={totalWorkingDaysType}
                  suffixIcon={<SelectDropdownLgIcon />}
                  style={{ width: 360 }}
                  value={working_days_type}
                  onChange={(val) => setWorking_days_type(val)}
                />

                <p className="text-body-text text-sm mt-2">
                  Total days shall be same for every pay period
                </p>

                {working_days_type === "as_per_calendar" ? (
                  <div className="my-4">
                    <Checkbox
                      className="text-header text-sm font-medium"
                      checked={is_weekend_included}
                      // onChange={(e) => setIs_weekend_included(e.target.checked)}
                      onChange={handleWeekendCheckboxChange}
                    >
                      Including weekends -{" "}
                      <span className="text-[#88909f] font-normal">
                        If checked, weekends shall be counted as working days
                      </span>
                    </Checkbox>
                  </div>
                ) : (
                  <div className="my-4 flex items-center gap-2">
                    <InputNumber
                      value={daysCount}
                      onChange={(val) => setDaysCount(val)}
                      style={{ maxWidth: 50 }}
                    />
                    <div className="text-header text-sm font-medium">
                      Days -{" "}
                      <span className="text-[#88909f] font-normal">
                        All per day calculations shall be done with this value
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* <div className="sticky bottom-0 py-4 bg-white border-t border-extra-light">
              <Button
                type="primary"
                className="px-10 mt-2"
                onClick={handleSubmit}
                loading={isAddLoading}
              >
                Save
              </Button>
            </div> */}
          </>
        )}

        <div className="sticky bottom-0 pt-4 pb-6 bg-white border-t border-extra-light">
          <Button
            type="primary"
            className="px-10 mt-2"
            onClick={handleSubmit}
            loading={isAddLoading}
          >
            Save
          </Button>
        </div>
      </div>
    </SidebarLayout>
  );
}

const daysList = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const totalWorkingDaysType = [
  {
    label: "As Per Calendar",
    value: "as_per_calendar",
  },
  {
    label: "Fixed Days",
    value: "fixed",
  },
];

const weekendPatternList = [
  {
    label: "Regular",
    value: "regular",
  },
  {
    label: "Even Weeks",
    value: "even_weeks",
  },
  {
    label: "Odd Weeks",
    value: "odd_weeks",
  },
];

const weekendPatternTitle = {
  regular: "Weekends occur every week of the month.",
  even_weeks:
    "Weekends occur only on the 2nd, 4th, and 6th weeks of the month.",
  odd_weeks: "Weekends occur only on the 1st, 3rd, and 5th weeks of the month.",
} as const;
